import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import {
  ChoiceGroup,
  FontWeights,
  IButtonProps,
  IButtonStyles,
  IChoiceGroupOption,
  ISeparatorStyles,
  IStackProps,
  IStackTokens,
  IToggleProps,
  IToggleStyles,
  Separator as FluentSeparator,
  Stack,
  Toggle as FluentToggle,
} from '@fluentui/react';
import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import { ActionButton as CommonActionButton } from 'common/components/Buttons/v8/ActionButton';
import { FamilyPersonaList } from 'common/components/FamilyPersonaList';
import { Panel } from 'common/components/Panel';
import { ModalShimmer } from 'common/components/Shimmer/DefaultShimmers/ModalShimmer';
import { Heading3, Text } from 'common/components/Text';
import { pageDataService } from 'common/utilities';
import { isITPFamilyApiEnabled, isITPSupported } from 'common/utilities/FlightSetting';
import { logClick } from 'common/utilities/telemetryHelpers';
import { useLocalizedStrings } from 'i18n';
import { debounce } from 'lodash';
import { FamilyPermission, FamilySharingResponse, ITPFamilyPermissionType } from 'mcsclient/models';
import { identityOnboardingPath } from 'Routes';
import { useAppDispatch, useAppSelector, useIdentityOnboardingStatus } from 'store';
import { getFamilyMembersAsync } from 'store/family/familySlice';
import { useNotifications } from 'store/layout/hooks/useNotifications';
import { useImmer } from 'use-immer';

import { IOfficeShellPanelProps } from './OfficeShellPanel.types';

const sectionTokens: IStackTokens = { childrenGap: 16 };
const Section = (props: IStackProps) => <Stack {...props} tokens={sectionTokens} />;

const toggleStyles: Partial<IToggleStyles> = { label: { fontWeight: FontWeights.regular } };
const Toggle = (props: IToggleProps) => <FluentToggle {...props} inlineLabel styles={toggleStyles} />;

const separatorStyles: Partial<ISeparatorStyles> = { root: { height: 1, padding: 0 } };
const Separator = () => <FluentSeparator styles={separatorStyles} />;

const actionButtonStyles: Partial<IButtonStyles> = {
  root: { minHeight: 'initial !important' },
  flexContainer: { padding: '6px 0' },
  label: { margin: 0 },
};
const ActionButton = (props: IButtonProps) => <CommonActionButton {...props} styles={actionButtonStyles} />;

const containerTokens: IStackTokens = { childrenGap: 32 };

const familyURL = 'https://family.microsoft.com';

type SettingsContext = {
  hasFamily: boolean;
  permissions?: FamilySharingResponse;
  isUnderage?: boolean;
  isOrganizer?: boolean;
};

type ControlStates = {
  familySharingToggle: boolean;
  deviceStatusToggle: boolean;
  identityPermissionChoice: ITPFamilyPermissionType;
};

const updateFamilySharingPermissions = debounce(
  (value: boolean, onFulfilled: (response: FamilySharingResponse) => void, onRejected: (error: unknown) => void) =>
    microsoftConsumerSecurityClient.familyPermissions
      .updateFamilySharingPermissions({ body: { value } })
      .then(onFulfilled, onRejected),
  500
);

const updateDeviceStatusPermission = debounce(
  (value: boolean, onFulfilled: (response: FamilyPermission) => void, onRejected: (error: unknown) => void) =>
    microsoftConsumerSecurityClient.familyPermissions
      .updateFamilyPermission({
        body: { familyPermissionType: 'DevicesStatus' as const, value },
      })
      .then(onFulfilled, onRejected),
  500
);

const updateItpFamilyPermission = debounce(
  (value: ITPFamilyPermissionType, onFulfilled: () => void, onRejected: (error: unknown) => void) =>
    microsoftConsumerSecurityClient.familyPermissions.updateItpFamilyPermission({ body: { value } }).then(onFulfilled, onRejected),
  500
);

const FamilySettings: React.FC = () => {
  const { t } = useLocalizedStrings();
  const dispatch = useAppDispatch();
  const { toastGenericError } = useNotifications();
  const history = useHistory();
  const { itmOnboarded } = useIdentityOnboardingStatus(pageDataService.getUserData().csid);

  const familyMembers = useAppSelector((state) => state.family.familyMembers);

  // Stores permissions and derived flags from API responses
  const [context, setContext] = useImmer<SettingsContext | undefined>(undefined);

  // Stores states that drive the UI controls
  const [controlStates, setControlStates] = useImmer<ControlStates | undefined>(undefined);

  const isLoading = !(context && controlStates);

  /**
   * Updating `controlStates` based on the current `context`
   */
  const syncControlStates = useCallback(() => {
    setControlStates({
      familySharingToggle: context?.permissions?.value ?? false,
      deviceStatusToggle: context?.permissions?.devicePermission?.value ?? false,
      identityPermissionChoice: context?.permissions?.itpFamilyPermissions?.value ?? 'NONE',
    });
  }, [
    context?.permissions?.devicePermission?.value,
    context?.permissions?.itpFamilyPermissions?.value,
    context?.permissions?.value,
    setControlStates,
  ]);

  useEffect(() => {
    if (context !== undefined && controlStates === undefined) {
      // initial sync
      syncControlStates();
    }
  }, [context, controlStates, syncControlStates]);

  /**
   * Error handler for API calls
   */
  const onRejected = useCallback(
    (eventName: string) => (error: unknown) => {
      toastGenericError(error, undefined, { EventName: eventName });
      // re-sync control states if API call failed
      syncControlStates();
    },
    [syncControlStates, toastGenericError]
  );

  useEffect(() => {
    if (familyMembers === undefined) {
      dispatch(getFamilyMembersAsync());
    } else {
      const hasFamily = familyMembers.length > 0; // API returns an empty array if family is not set up yet

      if (hasFamily) {
        microsoftConsumerSecurityClient.familyPermissions.getFamilySharingPermissions().then((permissions) => {
          setContext({
            hasFamily,
            permissions,
            isUnderage: !permissions.canEdit,
            isOrganizer: familyMembers.some((member) => member.role === 'Admin' && pageDataService.isCurrentUserByUserId(member.puid)),
          });
        });
      } else {
        setContext({ hasFamily });
      }
    }
  }, [dispatch, familyMembers, setContext]);

  /**
   * Set up family
   */
  const createFamilyGroup = useMemo(
    () => (
      <Section>
        <Text variant="Caption2">{t('OfficeShellPanel.FamilySettings.NotPartOfFamily')}</Text>
        <ActionButton
          href={familyURL}
          target="_blank"
          onClick={logClick('CreateFamilyGroup', () => {})}
          aria-label={t('OfficeShellPanel.FamilySettings.CreateAFamilyGroupAriaLabel')}
        >
          {t('OfficeShellPanel.FamilySettings.CreateAFamilyGroup')}
        </ActionButton>
      </Section>
    ),
    [t]
  );

  /**
   * Allow family sharing
   */
  const familySharingSection = useMemo(() => {
    const onChange = (event: React.MouseEvent<HTMLElement>, checked?: boolean) => {
      logClick(`FamilySharing${checked ? 'Enabled' : 'Disabled'}`, () => {})();

      setControlStates({
        familySharingToggle: !!checked,
        deviceStatusToggle: !!checked,
        identityPermissionChoice: 'NONE',
      });

      updateFamilySharingPermissions(
        !!checked,
        // onFulfilled
        (permissions) => {
          setContext((draft) => {
            if (draft) {
              draft.permissions = permissions;
            }
          });
        },
        onRejected('FailedToToggleFamilySharing' as const)
      );
    };

    return (
      <Section>
        <Toggle
          label={t('OfficeShellPanel.FamilySettings.AllowFamilySharing')}
          onChange={onChange}
          checked={controlStates?.familySharingToggle}
        />
        {!controlStates?.familySharingToggle && (
          <Text variant="Caption2">{t('OfficeShellPanel.FamilySettings.AllowFamilySharingExplain')}</Text>
        )}
      </Section>
    );
  }, [controlStates?.familySharingToggle, onRejected, setContext, setControlStates, t]);

  /**
   * Share device status
   */
  const deviceSection = useMemo(() => {
    const onChange = (event: React.MouseEvent<HTMLElement>, checked?: boolean) => {
      logClick(`FamilySettings${checked ? 'Allow' : 'Dont'}ShareSecurityStatus`, () => {})();

      setControlStates((draft) => {
        if (draft) {
          draft.deviceStatusToggle = !!checked;
        }
      });

      updateDeviceStatusPermission(
        !!checked,
        // onFulfilled
        (permissions) => {
          setContext((draft) => {
            if (draft?.permissions) {
              draft.permissions.devicePermission = permissions;
            }
          });
        },
        onRejected('FailedToToggleDeviceSharing' as const)
      );
    };

    return (
      <Section>
        <Heading3 variant="Subtitle2">{t('OfficeShellPanel.FamilySettings.ShareDeviceStatus')}</Heading3>
        <Text variant="Caption2">{t('OfficeShellPanel.FamilySettings.ShareDeviceStatusExplain')}</Text>
        {context?.isUnderage ? (
          <Text variant="Caption1Strong">{t('OfficeShellPanel.FamilySettings.DeviceSharingUnderage')}</Text>
        ) : (
          <Toggle
            label={t('OfficeShellPanel.FamilySettings.ShareMyDeviceStatus')}
            onChange={onChange}
            checked={controlStates?.deviceStatusToggle}
          />
        )}
      </Section>
    );
  }, [context?.isUnderage, controlStates?.deviceStatusToggle, onRejected, setContext, setControlStates, t]);

  /**
   * Share identity info
   */
  const itpSection = useMemo(() => {
    const options: (IChoiceGroupOption & { key: ITPFamilyPermissionType })[] = [
      { key: 'SEE_MY_ALERTS_AND_ADD_INFO', text: t('OfficeShellPanel.FamilySettings.SeeMyAlertsAndAddInfo') },
      { key: 'ADD_MY_INFO', text: t('OfficeShellPanel.FamilySettings.AddInfo') },
      { key: 'NONE', text: t('OfficeShellPanel.FamilySettings.None') },
    ];

    const onChange = (event?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => {
      const value = options.find((o) => o.key === option?.key)?.key;
      if (value) {
        logClick(
          {
            SEE_MY_ALERTS_AND_ADD_INFO: 'AlertPlusAddInfoSettingSelected', // eslint-disable-line @typescript-eslint/naming-convention
            ADD_MY_INFO: 'AddInfoSettingOnlySelected', // eslint-disable-line @typescript-eslint/naming-convention
            NONE: 'NoITPSharingSettingSelected',
          }[value],
          () => {}
        )();

        setControlStates((draft) => {
          if (draft) {
            draft.identityPermissionChoice = value;
          }
        });

        updateItpFamilyPermission(
          value,
          // onFulfilled
          () => {
            setContext((draft) => {
              if (draft?.permissions?.itpFamilyPermissions) {
                draft.permissions.itpFamilyPermissions.value = value;
              }
            });
          },
          onRejected('FailedToToggleITPSharing' as const)
        );
      }
    };

    if (context?.isUnderage) {
      return (
        <Section>
          <Heading3 variant="Subtitle2">{t('OfficeShellPanel.FamilySettings.ShareIdentityInfo')}</Heading3>
          <Text variant="Caption2">{t('OfficeShellPanel.FamilySettings.ShareIdentityInfoExplain')}</Text>
          <Text variant="Caption1Strong">{t('OfficeShellPanel.FamilySettings.IdentitySharingUnderage')}</Text>
        </Section>
      );
    }

    return itmOnboarded ? (
      <Section>
        <Heading3 variant="Subtitle2">{t('OfficeShellPanel.FamilySettings.ShareIdentityInfo')}</Heading3>
        <Text variant="Caption2">{t('OfficeShellPanel.FamilySettings.ShareIdentityInfoExplain')}</Text>
        <ChoiceGroup options={options} onChange={onChange} selectedKey={controlStates?.identityPermissionChoice} />
      </Section>
    ) : (
      <Section>
        <Heading3 variant="Subtitle2">{t('OfficeShellPanel.FamilySettings.ShareIdentityInfo')}</Heading3>
        <ActionButton
          onClick={logClick('FamilySettingsSetUpIdentityMonitoring', () => {
            history.push(identityOnboardingPath);
          })}
        >
          {t('OfficeShellPanel.FamilySettings.SetUpIdentityMonitoring')}
        </ActionButton>
        <Text variant="Caption2">{t('OfficeShellPanel.FamilySettings.SetUpIdentityMonitoringExplain')}</Text>
      </Section>
    );
  }, [
    context?.isUnderage,
    controlStates?.identityPermissionChoice,
    history,
    itmOnboarded,
    onRejected,
    setContext,
    setControlStates,
    t,
  ]);

  /**
   * Organizers list
   */
  const organizersSection = useMemo(
    () => (
      <Section>
        <Heading3 variant="Subtitle2">{t('OfficeShellPanel.FamilySettings.YourFamilyOrganizers')}</Heading3>
        <FamilyPersonaList role={'organizer' as const} />
      </Section>
    ),
    [t]
  );

  /**
   * Members list
   */
  const membersSections = useMemo(
    () => (
      <Section>
        <Stack>
          <Heading3 variant="Subtitle2">{t('OfficeShellPanel.FamilySettings.YourFamilyMembers')}</Heading3>
          <Text variant="Caption1Strong">{t('OfficeShellPanel.FamilySettings.MemberCannotSeeYourInfo')}</Text>
        </Stack>
        <FamilyPersonaList role={'member' as const} />
      </Section>
    ),
    [t]
  );

  /**
   * Link to family management
   */
  const manageYourFamilyGroup = useMemo(
    () => (
      <Section>
        <ActionButton
          href={familyURL}
          target="_blank"
          onClick={logClick('FamilySettingsManageYourFamilyGroup', () => {})}
          aria-label={t('OfficeShellPanel.FamilySettings.ManageYourFamilyGroupAriaLabel')}
        >
          {t('OfficeShellPanel.FamilySettings.ManageYourFamilyGroup')}
        </ActionButton>
      </Section>
    ),
    [t]
  );

  if (isLoading) {
    return <ModalShimmer />;
  }

  return (
    <Stack tokens={containerTokens}>
      {context.hasFamily ? (
        <>
          {!context.isUnderage && familySharingSection}
          {controlStates.familySharingToggle && (
            <>
              {deviceSection}
              {isITPSupported() && isITPFamilyApiEnabled() && (
                <>
                  <Separator />
                  {itpSection}
                </>
              )}
              <Separator />
              {organizersSection}
              {membersSections}
              {context.isOrganizer && manageYourFamilyGroup}
            </>
          )}
        </>
      ) : (
        createFamilyGroup
      )}
    </Stack>
  );
};

export const FamilySettingsPanel = (props: IOfficeShellPanelProps<'FamilySettings'>) => {
  const { t } = useLocalizedStrings();
  const { closeButtonAriaLabel, onDismiss, ...otherProps } = props;

  return (
    <Panel
      isBlocking
      isLightDismiss
      onDismiss={onDismiss}
      closeButtonAriaLabel={closeButtonAriaLabel}
      styles={{ content: { paddingLeft: 24, paddingRight: 24 } }}
      headerText={t('OfficeShellPanel.FamilySettings.Header')}
      {...otherProps}
    >
      <FamilySettings />
    </Panel>
  );
};
